import { supabase, ensureAuth, getWorkspaceId } from "./supabase.js";

// In-memory cache for stale-while-revalidate reads. Lives for the SPA session,
// scoped implicitly to the current workspace (cleared via store.clearCache()
// when the active workspace changes). Updated on every successful get/getMany/
// set/delete so it never goes stale relative to our own writes.
const _cache = new Map();

// ── localStorage fallback ─────────────────────────────────────────────────────
const PREFIX = "rai_";

const lsStore = {
  async get(key) {
    try { const r = localStorage.getItem(PREFIX + key); return r !== null ? JSON.parse(r) : null; }
    catch { return null; }
  },
  async getMany(keys) {
    const map = {};
    for (const k of keys) {
      try { const r = localStorage.getItem(PREFIX + k); if (r !== null) map[k] = JSON.parse(r); } catch {}
    }
    return map;
  },
  async set(key, val) {
    try { localStorage.setItem(PREFIX + key, JSON.stringify(val)); }
    catch (e) { console.warn("[store] write:", key, e); }
  },
  async delete(key) { try { localStorage.removeItem(PREFIX + key); } catch {} },
  async list(prefix = "") {
    try {
      return Object.keys(localStorage)
        .filter(k => k.startsWith(PREFIX + prefix))
        .map(k => k.slice(PREFIX.length));
    } catch { return []; }
  },
  async clear() {
    Object.keys(localStorage).filter(k => k.startsWith(PREFIX)).forEach(k => localStorage.removeItem(k));
  },
};

// ── Supabase store — keyed by workspace_id so each business has isolated data ─
const sbStore = {
  async get(key) {
    try {
      const wid = await getWorkspaceId();
      if (!wid) return null;
      const { data } = await supabase
        .from("app_data")
        .select("value")
        .eq("workspace_id", wid)
        .eq("key", key)
        .single();
      const value = data?.value ?? null;
      _cache.set(key, value);
      return value;
    } catch { return null; }
  },
  // Batched fetch — single Supabase round-trip instead of N. Returns a Promise of
  // an object map { key: value }, with missing keys absent. Modules like CRM that
  // need many app_data rows on mount should prefer this over multiple .get calls
  // (was 10 round-trips → 1).
  async getMany(keys) {
    try {
      const wid = await getWorkspaceId();
      if (!wid) return {};
      const { data } = await supabase
        .from("app_data")
        .select("key, value")
        .eq("workspace_id", wid)
        .in("key", keys);
      const map = {};
      for (const row of (data || [])) {
        map[row.key] = row.value;
        _cache.set(row.key, row.value);
      }
      // Cache nulls for keys that returned no row, so peekMany knows we've already
      // checked and there's genuinely nothing there.
      for (const k of keys) if (!(k in map)) _cache.set(k, null);
      return map;
    } catch { return {}; }
  },
  // Sync read from the in-memory cache. Returns a map containing only keys that
  // have been fetched (or written) at least once during this session. Use this
  // for instant-render UI before an async getMany refresh lands.
  peekMany(keys) {
    const map = {};
    for (const k of keys) if (_cache.has(k)) map[k] = _cache.get(k);
    return map;
  },
  // Drop the in-memory cache — call when the active workspace changes so the
  // next read fetches fresh data from the new workspace instead of returning
  // stale values from the previous one.
  clearCache() { _cache.clear(); },
  async set(key, val) {
    // Update cache synchronously so subsequent peekMany reflects this write
    // immediately, even if the network write below is still in flight.
    _cache.set(key, val);
    try {
      const wid = await getWorkspaceId();
      const uid = await ensureAuth();
      if (!wid) return;
      const ts = new Date().toISOString();
      // Check if row exists, then update or insert — avoids partial-index upsert issues
      const { data: existing } = await supabase
        .from("app_data").select("id").eq("workspace_id", wid).eq("key", key).maybeSingle();
      if (existing?.id) {
        const { error } = await supabase.from("app_data")
          .update({ value: val, updated_at: ts })
          .eq("id", existing.id);
        if (error) console.warn("[store] update failed:", key, error.message);
      } else {
        const { error } = await supabase.from("app_data")
          .insert({ user_id: uid, workspace_id: wid, key, value: val, updated_at: ts });
        if (error) console.warn("[store] insert failed:", key, error.message);
      }
    } catch (e) { console.warn("[store] write failed:", key, e); }
  },
  async delete(key) {
    _cache.delete(key);
    try {
      const wid = await getWorkspaceId();
      if (!wid) return;
      await supabase.from("app_data").delete().eq("workspace_id", wid).eq("key", key);
    } catch {}
  },
  async list(prefix = "") {
    try {
      const wid = await getWorkspaceId();
      if (!wid) return [];
      const { data } = await supabase
        .from("app_data")
        .select("key")
        .eq("workspace_id", wid)
        .like("key", `${prefix}%`);
      return (data || []).map(r => r.key);
    } catch { return []; }
  },
  async clear() {
    _cache.clear();
    try {
      const wid = await getWorkspaceId();
      if (!wid) return;
      await supabase.from("app_data").delete().eq("workspace_id", wid);
    } catch {}
  },
};

// Add cache-friendly methods to the localStorage fallback too so callers don't
// need to know which backend is active.
lsStore.peekMany = function (keys) {
  const map = {};
  for (const k of keys) {
    try { const r = localStorage.getItem(PREFIX + k); if (r !== null) map[k] = JSON.parse(r); } catch {}
  }
  return map;
};
lsStore.clearCache = function () { /* no-op — localStorage is the cache */ };

export const store = supabase ? sbStore : lsStore;
